import { Link } from "react-router-dom";

export default function DesignCustomRugbyUniformsNZ() {
  return (
    <>
      <p>
        So your club has decided it's time for a new kit. Maybe last
        season's jersey is faded and stretched out, maybe your old supplier
        closed up shop, or maybe you've simply outgrown the design. Whatever
        the trigger, designing <strong>custom rugby uniforms in NZ</strong>{" "}
        is exciting — and a bit overwhelming. Where do you start? What
        decisions matter? How do you make sure the kit your committee
        approves looks great on Saturday afternoon under floodlights?
      </p>
      <p>
        This guide walks you through the practical stuff: colours, patterns,
        fabric, sizing, print method, sponsor placement, and the approval
        process. We've designed kits for everyone from Premier 1 senior
        clubs to under-7 minis, so this is the playbook we'd give a club
        committee starting from a blank page.
      </p>

      <h2>Step 1 — Define your team's identity</h2>
      <p>
        Before you talk fabric or print method, sit down with your committee
        and answer three questions: <em>Who are we? What are our colours?
        What do we stand for?</em> The answers should drive every design
        decision that follows. A senior club steeped in 80 years of history
        wants a kit that nods to that. A new high-school first XV wants
        something fresh and modern that announces them. A church rugby team
        wants their faith and culture visible. Your kit is shorthand for
        all of that.
      </p>
      <p>
        Document this on one page. Two paragraphs is plenty. Your designer
        will use it as the brief.
      </p>

      <h2>Step 2 — Lock in your colours</h2>
      <p>
        Most clubs already have a primary and secondary colour. If you
        don't, pick a strong primary that reads from 50 metres away (deep
        blacks, navies, maroons and bottle greens are perennial winners),
        and a sharp accent that contrasts cleanly. Avoid muddy mid-tones
        that look fine on a screen but disappear on grass.
      </p>
      <p>
        If you're working in cultural design — Pasifika or Māori patterning
        — earthy tones (rust, cream, charcoal) often pair better with
        traditional motifs than fluoro accents. Talk to your designer about
        which palette will let your patterns sing.
      </p>

      <h2>Step 3 — Choose your design direction</h2>
      <p>
        At this stage you're picking between three or four broad
        directions:
      </p>
      <ul>
        <li>
          <strong>Heritage / classic.</strong> Solid block colours, simple
          chevron or stripe, traditional collar. Looks great forever.
        </li>
        <li>
          <strong>Modern athletic.</strong> Asymmetric panels, body-mapped
          colour blocks, modern collar. Reads as professional and
          contemporary.
        </li>
        <li>
          <strong>Cultural.</strong> Tatau, kowhaiwhai, siapo or geometric
          Polynesian motifs integrated tastefully into the layout.
        </li>
        <li>
          <strong>Hybrid.</strong> Most of our work — a modern athletic
          base with cultural patterns layered into specific panels.
        </li>
      </ul>

      <h2>Step 4 — Decide on print method</h2>
      <p>
        For senior club rugby, the answer is almost always{" "}
        <strong>full sublimation</strong>. The dye becomes part of the
        fabric, can't crack or peel, and lets you do photographic-quality
        patterns and gradients. (We've written a separate guide on{" "}
        <Link to="/blog/sublimation-vs-dtf-printing">sublimation vs DTF</Link>{" "}
        if you want to go deeper.)
      </p>

      <h2>Step 5 — Fabric weight matters</h2>
      <p>
        New Zealand winters are wet. Your fabric needs to handle it. We
        recommend:
      </p>
      <ul>
        <li>
          <strong>180–200gsm interlock</strong> — premium feel, classic
          rugby weight, holds shape after washing.
        </li>
        <li>
          <strong>140–160gsm performance mesh</strong> — lighter, cooler,
          better for sevens and warmer climates.
        </li>
        <li>
          <strong>Reinforced shoulder seams</strong> — non-negotiable for
          contact rugby.
        </li>
      </ul>

      <h2>Step 6 — Sponsor logos &amp; sizing</h2>
      <p>
        Get sponsor approval and high-res logo files before final artwork.
        Plan placement during design — chest, sleeve, back-of-neck — and
        make sure logos are sized consistently. For sizing, run a fit kit
        across your squad before you commit to a final order. A 25-shirt
        sizing exercise saves you 5+ returns later.
      </p>

      <h2>Step 7 — Approval &amp; production</h2>
      <p>
        Once your designer sends mockups, get every committee member to
        view them on a phone screen <em>and</em> printed at A4. What looks
        crisp on a Retina display can look fussy when printed at scale.
        Once the committee signs off, lock the artwork in writing — no more
        tweaks after this point or you'll blow your timeline.
      </p>

      <h2>How long does it take?</h2>
      <p>
        Plan for 3–6 weeks total: 1–2 weeks of design, 1 week of revisions
        and approval, 2–3 weeks of production. If you want kits ready for
        round 1, start the conversation in January at the latest.
      </p>

      <h2>Ready to start your design?</h2>
      <p>
        Head over to our{" "}
        <Link to="/custom-rugby-uniforms-nz">custom rugby uniforms NZ</Link>{" "}
        page or fill out the form below. We'll send back a free design
        concept and quote within 24 hours.
      </p>
    </>
  );
}
